import { Host, h } from "@stencil/core";
import { RADIO_ITEM_PARTS_DICTIONARY } from "../../common/reserved-names";
const PARTS = (checked, disabled) => {
    const checkedValue = checked
        ? RADIO_ITEM_PARTS_DICTIONARY.CHECKED
        : RADIO_ITEM_PARTS_DICTIONARY.UNCHECKED;
    return disabled
        ? `${RADIO_ITEM_PARTS_DICTIONARY.DISABLED} ${checkedValue}`
        : checkedValue;
};
/**
 * The radio group control is used to render a short list of mutually exclusive options.
 *
 * It contains radio items to allow users to select one option from the list of options.
 *
 * @part radio__item - The radio item element.
 * @part radio__container - The container that serves as a wrapper for the `input` and the `option` parts.
 * @part radio__input - The invisible input element that implements the interactions for the component. This part must be kept "invisible".
 * @part radio__option - The actual "input" that is rendered above the `input` part. This part has `position: absolute` and `pointer-events: none`.
 * @part radio__label - The label that is rendered when the `caption` property is not empty.
 *
 * @part checked - Present in the `radio__item`, `radio__option`, `radio__label` and `radio__container` parts when the control is checked (`checked` === `true`).
 * @part disabled - Present in the `radio__item`, `radio__option`, `radio__label` and `radio__container` parts when the control is disabled (`disabled` === `true`).
 * @part unchecked - Present in the `radio__item`, `radio__option`, `radio__label` and `radio__container` parts when the control is not checked (`checked` !== `true`).
 */
export class ChRadioGroupRender {
    constructor() {
        this.direction = "horizontal";
        this.disabled = false;
        this.model = undefined;
        this.value = undefined;
    }
    internals;
    handleValueChange(newValue) {
        // Update form value
        this.internals.setFormValue(newValue);
    }
    /**
     * Fired when the selected item change. It contains the information about the
     * new selected value.
     */
    change;
    #handleCheckedInputChange = (value) => (event) => {
        event.stopPropagation();
        this.value = value;
        this.change.emit(value);
    };
    #itemRender = (item, index) => {
        const checked = this.value === item.value;
        const disabled = item.disabled || this.disabled;
        const additionalParts = PARTS(checked, disabled);
        return (h("div", { class: "radio-item", part: `${RADIO_ITEM_PARTS_DICTIONARY.RADIO_ITEM} ${additionalParts}` }, h("div", { class: {
                container: true,
                "container--checked": checked
            }, part: `${RADIO_ITEM_PARTS_DICTIONARY.CONTAINER} ${additionalParts}` }, h("input", { id: item.caption ? `radio-item-${index}` : null, name: "radio-group", "aria-label": !item.caption ? item.accessibleName : null, class: { input: true, "input--enabled": !disabled }, part: RADIO_ITEM_PARTS_DICTIONARY.INPUT, type: "radio", checked: checked, disabled: disabled, value: item.value, onInput: this.#handleCheckedInputChange(item.value) }), h("div", { class: {
                option: true,
                "option--unchecked": !checked
            }, part: `${RADIO_ITEM_PARTS_DICTIONARY.OPTION} ${additionalParts}`, "aria-hidden": "true" })), item.caption && (h("label", { class: { label: true, "label--enabled": !disabled }, part: `${RADIO_ITEM_PARTS_DICTIONARY.LABEL} ${additionalParts}`, htmlFor: `radio-item-${index}` }, item.caption))));
    };
    connectedCallback() {
        // Set form value
        this.internals.setFormValue(this.value);
    }
    render() {
        return (h(Host, { key: '434e87a1683f81831efd80a28948f9f63604d404', role: "radiogroup", class: `ch-radio-group--direction-${this.direction}` }, this.model?.map(this.#itemRender)));
    }
    static get is() { return "ch-radio-group-render"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["radio-group-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["radio-group-render.css"]
        };
    }
    static get properties() {
        return {
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"horizontal\" | \"vertical\"",
                    "resolved": "\"horizontal\" | \"vertical\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the direction of the items."
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "\"horizontal\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the radio-group is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "RadioGroupModel",
                    "resolved": "RadioGroupItemModel[]",
                    "references": {
                        "RadioGroupModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/radio-group/types.ts::RadioGroupModel"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the items of the ch-radio-group-render control."
                }
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the control."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "change",
                "name": "change",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected item change. It contains the information about the\nnew selected value."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "handleValueChange"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
